'use client;';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Skeleton } from '@/components/ui/skeleton';
import { useEditableState } from '@/hooks/use-editable-state';
import { isValidUSPhoneNumber, normalizeUSPhoneNumber } from '@/lib/phone';

type VoiceSettings = ApiSchemas['OfficeVoiceSettingsRead'];
type VoiceAgent = ApiSchemas['VoiceAgentRead'];

type Tab = 'general' | 'existing_customer' | 'new_customer' | 'call_routing' | 'behaviors';

const destinationSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('TRANSFER'),
    value: z
      .string()
      .min(1, 'Phone number is required')
      .refine(isValidUSPhoneNumber, 'Please enter a valid US phone number')
      .transform((val) => normalizeUSPhoneNumber(val) || val),
    execution_message: z.string().optional().default(''),
  }),
  z.object({
    type: z.literal('EMAIL'),
    value: z.string().min(1, 'Email is required').email('Invalid email'),
    execution_message: z.string().optional().default(''),
  }),
]);

const schema = z.object({
  general: z
    .object({
      agent_name: z.string().min(1, 'Name is required'),
      intro_in_hours: z.string().min(1, 'Intro message is required'),
      intro_after_hours: z.string().min(1, 'Intro message is required'),
      language: z.enum(['English', 'Spanish', 'English and Spanish']).optional(),
    })
    .passthrough(),
  agent: z
    .object({
      reminder_max_count: z
        .number()
        .min(0, 'Reminder count must be greater than 0')
        .max(10, 'Reminder count must be less than 10'),
    })
    .passthrough(),
  escalation: z
    .object({
      policy_in_hours: destinationSchema,
      policy_after_hours: destinationSchema,
    })
    .passthrough(),
  routing: z
    .object({
      rules: z.array(
        z.object({
          name: z.string(),
          description: z.string(),
          destination_in_hours: destinationSchema.nullable(),
          destination_after_hours: destinationSchema.nullable(),
        })
      ),
      unavailable_script: z.string(),
    })
    .passthrough(),
  notifications: z
    .object({
      global_recipients: z.array(z.string().email()).default([]),
      include: z.object({
        first_name: z.boolean().default(true),
        last_name: z.boolean().default(true),
        phone_number: z.boolean().default(true),
        email_address: z.boolean().default(true),
        address: z.boolean().default(true),
        issue_type: z.boolean().default(true),
        summary: z.boolean().default(true),
        transcript: z.boolean().default(true),
        recording_url: z.boolean().default(true),
      }),
      scenarios: z
        .array(
          z.object({
            name: z.string().min(1, 'Name is required'),
            description: z.string().optional().default(''),
            recipients: z.array(z.string().email()).default([]),
          })
        )
        .default([]),
    })
    .passthrough()
    .optional(),
});

interface ContextType {
  agent: VoiceAgent | null;
  settings: VoiceSettings;
  errors: z.ZodFormattedError<z.infer<typeof schema>> | undefined;
  onChange: (settings: Partial<VoiceSettings>) => void;
  onSave: () => void;
  onCreateAgent: () => void;
  onPublishAgent: () => void;
  isChanged: boolean;
  isSaveLoading: boolean;
  isCreateAgentLoading: boolean;
  isPublishAgentLoading: boolean;
  tab: Tab;
  setTab: (tab: Tab) => void;
}

const VoiceSettingsContext = createContext<ContextType | null>(null);

export function useVoiceSettingsCtx() {
  const ctx = useContext(VoiceSettingsContext);
  if (!ctx) {
    throw new Error('useVoiceSettingsCtx must be used within a VoiceSettingsProvider');
  }
  return ctx;
}

interface Props {
  children: React.ReactNode;
}

export function VoiceSettingsProvider({ children }: Props) {
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const { data } = api.voiceSettings.getVoiceSettings.useQuery({
    path: { office_id: officeId },
  });

  if (!data) {
    return <Skeleton className='h-full w-full' />;
  }

  return <VoiceSettingProviderInner data={data}>{children}</VoiceSettingProviderInner>;
}

function VoiceSettingProviderInner({ data, children }: { data: VoiceSettings; children: React.ReactNode }) {
  const [tab, setTab] = useState<Tab>('general');

  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();

  const state = useEditableState(data);
  const errors = useMemo(() => {
    const result = schema.safeParse(state.value);
    if (result.success) {
      return result.error;
    }
    return result.error.format();
  }, [state.value]);

  useEffect(() => {
    if (state.value) return;
    if (data) {
      state.setValue(data);
    }
  }, [data, state]);

  const mutateVoiceSettings = api.voiceSettings.updateVoiceSettings.useMutation(
    {
      path: { office_id: officeId },
    },
    {
      onSuccess: (data) => {
        state.reset(data);
        toast.success('Voice settings updated');
      },
    }
  );

  const { data: agent, refetch: refetchVoiceAgent } = api.agentVoice.getDefaultVoiceAgent.useQuery(undefined, {
    retry: false,
  });
  const createAgent = api.agentVoice.createDefaultVoiceAgent.useMutation(
    {},
    {
      onSuccess: () => {
        refetchVoiceAgent();
        toast.success('Voice agent created');
      },
      meta: {
        toastError: 'Failed to create voice agent.',
      },
    }
  );
  const publishAgent = api.agentVoice.publishVoiceAgent.useMutation(undefined, {
    onSuccess: () => {
      toast.success('Voice agent deployed');
    },
    meta: {
      toastError: 'Failed to deploy voice agent.',
    },
  });

  const onChange = (settings: Partial<VoiceSettings>) => {
    state.setValue((prev) => ({ ...prev, ...settings }));
  };

  const onSave = () => {
    const result = schema.passthrough().safeParse(state.value);
    if (!result.success) {
      toast.error('Please provide all required fields');
      console.log(result.error);
      return;
    }

    console.log('voice settings', result.data);
    mutateVoiceSettings.mutate(result.data as VoiceSettings);
  };

  const onCreateAgent = () => {
    if (agent?.agent) return;
    createAgent.mutate();
  };

  const onPublishAgent = () => {
    const result = schema.safeParse(state.value);
    if (!result.success) {
      const formatted = result.error.format();
      if (formatted.escalation) {
        toast.error('Please provide all required default transfer values');
      } else {
        toast.error('Please provide all required fields');
      }
      return;
    }
    if (!agent || !agent.agent) {
      toast.error('Please create a voice agent first');
      return;
    }
    publishAgent.mutate({
      path: { id: agent.agent.id },
    });
  };

  return (
    <VoiceSettingsContext.Provider
      value={{
        agent: agent?.agent ?? null,
        settings: state.value,
        errors,
        tab,
        setTab,
        onChange,
        onSave,
        isSaveLoading: mutateVoiceSettings.isPending,
        onCreateAgent,
        isCreateAgentLoading: createAgent.isPending,
        onPublishAgent,
        isPublishAgentLoading: publishAgent.isPending,
        isChanged: state.isDirty,
      }}
    >
      {children}
    </VoiceSettingsContext.Provider>
  );
}
